import { useState, useEffect } from 'react';
import {
  Box,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  RadioGroup,
  FormControlLabel,
  Radio,
  FormLabel,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  Chip,
  Button,
  Tooltip,
} from '@mui/material';
import { ExpandMore, FilterList, Clear, Info } from '@mui/icons-material';

export interface SessionFilters {
  organizationId?: string;
  userId?: string;
  noteId?: string;
  startDate?: string;
  endDate?: string;
  dateField?: 'created_at' | 'updated_at' | 'sent_at';
  smsStatus?: 'all' | 'sent' | 'not_sent';
  feedbackStatus?: 'all' | 'has_feedback' | 'no_feedback';
}

interface JotCoachSessionFiltersProps {
  filters: SessionFilters;
  onChange: (filters: SessionFilters) => void;
  onClear: () => void;
}

export function JotCoachSessionFilters({
  filters,
  onChange,
  onClear,
}: JotCoachSessionFiltersProps) {
  const [expanded, setExpanded] = useState(false);
  const [localFilters, setLocalFilters] = useState<SessionFilters>(filters);

  useEffect(() => {
    setLocalFilters(filters);
  }, [filters]);

  const handleFilterChange = (key: keyof SessionFilters, value: string) => {
    const newFilters = { ...localFilters, [key]: value || undefined };
    setLocalFilters(newFilters);
    onChange(newFilters);
  };

  const handleDateChange = (startDate: string, endDate: string) => {
    const newFilters = { ...localFilters, startDate, endDate };
    setLocalFilters(newFilters);
    onChange(newFilters);
  };

  const activeFilterCount = Object.values(filters).filter(
    (v) => v !== undefined && v !== 'all',
  ).length;

  return (
    <Box mb={3}>
      <Accordion expanded={expanded} onChange={() => setExpanded(!expanded)}>
        <AccordionSummary expandIcon={<ExpandMore />}>
          <Box display="flex" alignItems="center" gap={2} width="100%">
            <FilterList color="primary" />
            <Typography variant="h6">Session Filters</Typography>
            {activeFilterCount > 0 && (
              <Chip
                label={`${activeFilterCount} active`}
                size="small"
                color="primary"
                variant="outlined"
              />
            )}
            {activeFilterCount > 0 && (
              <Button
                size="small"
                startIcon={<Clear />}
                onClick={(e) => {
                  e.stopPropagation();
                  onClear();
                }}
                sx={{ ml: 'auto' }}
              >
                Clear All
              </Button>
            )}
          </Box>
        </AccordionSummary>

        <AccordionDetails>
          <Box display="flex" flexDirection="column" gap={3}>
            {/* Organization and User Filters */}
            <Box display="flex" gap={2}>
              <TextField
                fullWidth
                label="Organization ID"
                size="small"
                value={localFilters.organizationId || ''}
                onChange={(e) => handleFilterChange('organizationId', e.target.value)}
                placeholder="Filter by organization..."
              />
              <TextField
                fullWidth
                label="User ID"
                size="small"
                value={localFilters.userId || ''}
                onChange={(e) => handleFilterChange('userId', e.target.value)}
                placeholder="Filter by user/provider..."
              />
            </Box>

            {/* Note ID Filter */}
            <TextField
              fullWidth
              label="Note ID"
              size="small"
              value={localFilters.noteId || ''}
              onChange={(e) => handleFilterChange('noteId', e.target.value)}
              placeholder="Search by note ID..."
            />

            {/* Date Range */}
            <Box>
              <FormLabel>Date Range</FormLabel>
              <Box display="flex" gap={2} mt={1}>
                <TextField
                  type="date"
                  label="Start Date"
                  size="small"
                  value={localFilters.startDate || ''}
                  onChange={(e) =>
                    handleDateChange(e.target.value, localFilters.endDate || '')
                  }
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                />
                <TextField
                  type="date"
                  label="End Date"
                  size="small"
                  value={localFilters.endDate || ''}
                  onChange={(e) =>
                    handleDateChange(localFilters.startDate || '', e.target.value)
                  }
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                />
              </Box>
            </Box>

            {/* Date Field Selector */}
            <FormControl component="fieldset" size="small">
              <Box display="flex" alignItems="center" gap={1} mb={1}>
                <FormLabel component="legend">Filter by Date Field</FormLabel>
                <Tooltip
                  title={
                    <Box>
                      <Typography variant="caption" display="block">
                        <strong>Created At:</strong> When session was generated
                      </Typography>
                      <Typography variant="caption" display="block">
                        <strong>Updated At:</strong> Last modification time
                      </Typography>
                      <Typography variant="caption" display="block">
                        <strong>SMS Sent At:</strong> When SMS was delivered (only shows sessions
                        where SMS was sent)
                      </Typography>
                    </Box>
                  }
                >
                  <Info fontSize="small" color="action" />
                </Tooltip>
              </Box>
              <RadioGroup
                row
                value={localFilters.dateField || 'created_at'}
                onChange={(e) =>
                  handleFilterChange('dateField', e.target.value)
                }
              >
                <FormControlLabel
                  value="created_at"
                  control={<Radio size="small" />}
                  label="Created At"
                />
                <FormControlLabel
                  value="updated_at"
                  control={<Radio size="small" />}
                  label="Updated At"
                />
                <FormControlLabel
                  value="sent_at"
                  control={<Radio size="small" />}
                  label="SMS Sent At"
                />
              </RadioGroup>
            </FormControl>

            {/* SMS Status Filter */}
            <FormControl component="fieldset" size="small">
              <FormLabel component="legend">SMS Status</FormLabel>
              <RadioGroup
                row
                value={localFilters.smsStatus || 'all'}
                onChange={(e) => handleFilterChange('smsStatus', e.target.value)}
              >
                <FormControlLabel value="all" control={<Radio size="small" />} label="All" />
                <FormControlLabel value="sent" control={<Radio size="small" />} label="Sent" />
                <FormControlLabel
                  value="not_sent"
                  control={<Radio size="small" />}
                  label="Not Sent"
                />
              </RadioGroup>
            </FormControl>

            {/* Feedback Status Filter */}
            <FormControl component="fieldset" size="small">
              <FormLabel component="legend">Feedback Status</FormLabel>
              <RadioGroup
                row
                value={localFilters.feedbackStatus || 'all'}
                onChange={(e) => handleFilterChange('feedbackStatus', e.target.value)}
              >
                <FormControlLabel value="all" control={<Radio size="small" />} label="All" />
                <FormControlLabel
                  value="has_feedback"
                  control={<Radio size="small" />}
                  label="Has Feedback"
                />
                <FormControlLabel
                  value="no_feedback"
                  control={<Radio size="small" />}
                  label="No Feedback"
                />
              </RadioGroup>
            </FormControl>
          </Box>
        </AccordionDetails>
      </Accordion>
    </Box>
  );
}
